/**
 * The measure session — `docs/property-maps-v2.md` §5.4.
 *
 * **Measure is a tape, not a drawing.** What somebody outlines here is not on
 * a layer, is never sent to the map endpoints, and has exactly one way to
 * become permanent: *Add to property measurements*, which writes a part and
 * leaves the shape where it is. So the shapes live on the client, and this is
 * where.
 *
 * **Zustand rather than context, because the reader is outside the tab.**
 * Everything else in this feature is inside `PropertyMapTab` and a context
 * would do — but the leave-guard is not: the Property record switches tabs
 * above it, and "you have 2 measurements that are not saved" has to be
 * answerable from there. A store is the one shape that answers both.
 *
 * **Mirrored to `sessionStorage`, per property.** A refresh in the middle of
 * measuring a site is not a reason to lose the morning; a different tab
 * measuring a different property is not the same session. Every read and
 * write is wrapped, because private mode, cleared site data and a thumbnail
 * capture all make storage throw rather than return nothing.
 */
import { create } from 'zustand'
import { uuidv7 } from 'uuidv7'
import type { FeatureKind, Geometry } from '@rowted/shared'

/** One outline on the tape. `partId` is set once it has been saved. */
export interface MeasureShape {
  id: string
  kind: FeatureKind
  geometry: Geometry
  /** A rect's two corners or a circle's centre and radius (§5.3). */
  shape?: unknown
  name: string
  /** The measurement type it will be saved under; null until somebody picks. */
  typeId: string | null
  /**
   * Whether this outline has been added to the property.
   *
   * A flag rather than the part's id, because `POST …/measurements` answers
   * with a count: inventing an id to hold here would be a field that reads
   * like a link to a row and is not one.
   */
  saved: boolean
}

export type NewShape = Omit<MeasureShape, 'id' | 'saved'>

const KEY = (propertyId: string) => `rowted.measure.${propertyId}`

function read(propertyId: string): MeasureShape[] {
  try {
    const raw = sessionStorage.getItem(KEY(propertyId))
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as MeasureShape[]) : []
  } catch {
    return []
  }
}

function write(propertyId: string, shapes: MeasureShape[]): void {
  try {
    if (shapes.length === 0) sessionStorage.removeItem(KEY(propertyId))
    else sessionStorage.setItem(KEY(propertyId), JSON.stringify(shapes))
  } catch {
    /* A session that cannot be mirrored still measures; it just does not
       survive a refresh, which is better than refusing to draw. */
  }
}

interface MeasureStore {
  /** Keyed by property, because two tabs can measure two sites. */
  byProperty: Record<string, MeasureShape[]>
  /** Read what `sessionStorage` has, once per property per page load. */
  hydrate: (propertyId: string) => void
  add: (propertyId: string, shape: NewShape) => MeasureShape
  patch: (propertyId: string, id: string, patch: Partial<MeasureShape>) => void
  remove: (propertyId: string, id: string) => void
  /** After a save: the outlines that are now on the property. */
  markSaved: (propertyId: string, ids: string[]) => void
  /** **Discard** — the session is over and nothing is kept. */
  clear: (propertyId: string) => void
}

export const useMeasureSession = create<MeasureStore>((set, get) => ({
  byProperty: {},

  hydrate: (propertyId) => {
    if (get().byProperty[propertyId]) return
    set((state) => ({ byProperty: { ...state.byProperty, [propertyId]: read(propertyId) } }))
  },

  add: (propertyId, shape) => {
    const made: MeasureShape = { ...shape, id: uuidv7(), saved: false }
    set((state) => {
      const next = [...(state.byProperty[propertyId] ?? []), made]
      write(propertyId, next)
      return { byProperty: { ...state.byProperty, [propertyId]: next } }
    })
    return made
  },

  patch: (propertyId, id, patch) => set((state) => {
    const next = (state.byProperty[propertyId] ?? [])
      .map((one) => (one.id === id ? { ...one, ...patch } : one))
    write(propertyId, next)
    return { byProperty: { ...state.byProperty, [propertyId]: next } }
  }),

  remove: (propertyId, id) => set((state) => {
    const next = (state.byProperty[propertyId] ?? []).filter((one) => one.id !== id)
    write(propertyId, next)
    return { byProperty: { ...state.byProperty, [propertyId]: next } }
  }),

  markSaved: (propertyId, ids) => set((state) => {
    const done = new Set(ids)
    const next = (state.byProperty[propertyId] ?? [])
      .map((one) => (done.has(one.id) ? { ...one, saved: true } : one))
    write(propertyId, next)
    return { byProperty: { ...state.byProperty, [propertyId]: next } }
  }),

  clear: (propertyId) => set((state) => {
    write(propertyId, [])
    return { byProperty: { ...state.byProperty, [propertyId]: [] } }
  }),
}))

/** The shapes on one property's tape, hydrating on the first look. */
export function measureShapesOf(propertyId: string): MeasureShape[] {
  return useMeasureSession.getState().byProperty[propertyId] ?? read(propertyId)
}

/**
 * How many outlines have not been added to the property.
 *
 * The one number the leave-guard, the panel footer and the strip all ask for
 * — a shape already added to the property loses nothing by being left, so it
 * does not count.
 */
export const unsavedCount = (shapes: MeasureShape[]): number =>
  shapes.filter((one) => !one.saved).length
